/* eslint-disable no-console */

import React, { useState, useCallback } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  Eye,
  Book,
  Wrench,
  Circle,
  HelpCircle,
  ChevronUp,
  FileText,
  Plus,
  Trash2,
  GripVertical,
  Play,
  Zap,
  Loader2
} from 'lucide-react';
import { GenerationSettings } from '../types/index.ts';
import { apiV1Url } from '../lib/apiBaseUrl.ts';
import { creatorFetch } from '../services/creatorFetch.ts';
import { createCorrelationId } from '../lib/correlation.ts';
import { useOrchestratorHealth } from '../hooks/useOrchestratorHealth.ts';
import { OrchestratorStatus } from './OrchestratorStatus.tsx';
// import GuidePreview from './GuidePreview';

interface GuideStep {
  id: string;
  order: number;
  title: string;
  title_bullets?: string[];
  description: string;
  warning?: string;
  visual_suggestion?: string;
  capture?: {
    type: 'photo' | 'video' | 'background';
    perspective: string;
    action: string;
    imageUrl?: string;
    colorHint?: string;
  };
  media?: {
    type: 'image' | 'video';
    url: string;
    description: string;
    is_default?: boolean;
    generated_at?: string;
  };
  step_titles?: Array<{
    icon_id: string;
    label: string;
    redirect?: string;
    render_style?: 'grayed_slash';
    context_tag?: string;
  }>;
  action_brief?: {
    mode: 'clip' | 'still' | 'skip';
    subject_action?: string;
    camera?: string;
    must_show?: string;
    must_not_show?: string;
    end_frame?: string;
    detail?: string;
  };
  fallback_image?: boolean;
}

interface GeneratedGuide {
  id: string;
  title: string;
  description: string;
  category: string;
  estimated_time?: string;
  difficulty: string;
  steps: GuideStep[];
  created_at: string;
  topic_id: string;
  pipeline_metadata?: Record<string, unknown>;
}

interface MakerProduct {
  productId: string;
  productName: string;
  productAlias?: string;
}

interface MakerBrand {
  brandId: string;
  brandName: string;
  brandAlias?: string;
  role?: string;
  products?: MakerProduct[];
}

interface GuideResultsProps {
  guides: GeneratedGuide[];
  productName: string;
  draftId?: string;
  onExportGuide: (guide: GeneratedGuide, format: 'pdf' | 'word' | 'html') => void;
  onBack: () => void;
  onStartOver: () => void;
  makerCreatorId?: string;
  makerBrands?: MakerBrand[];
  selectedMakerBrandId?: string;
  selectedMakerProductId?: string;
  onMakerTargetChange?: (brandId: string, productId: string) => void;
  generationSettings?: GenerationSettings;
  returnToEditorUrl?: string;
}

const isShelfWalkthrough = (guide: GeneratedGuide) =>
  guide.category === 'product_walkthrough_shelf' || guide.topic_id === 'product_walkthrough_shelf';

interface QaFlag {
  stepKey: string;
  issue: string;
}

// QA issues come from several sources with different shapes: post-generation
// prefilter issues are plain strings, but listing-extraction issues (and any
// other structured gate) are {check, field, detail} objects -- normalize
// every shape to a string before it ever reaches JSX, since React throws
// (minified error #31) if an object is rendered as a child directly.
const formatQaIssue = (issue: unknown): string => {
  if (typeof issue === 'string') return issue;
  if (issue && typeof issue === 'object') {
    const entry = issue as { check?: string; field?: string; detail?: string };
    const field = (entry.field || entry.check || '').trim();
    const detail = (entry.detail || '').trim();
    if (field && detail) return `${field} — ${detail}`;
    return field || detail || JSON.stringify(issue);
  }
  return String(issue);
};

// Post-generation QA issues are advisory, not blocking (a product still
// generates with a few flagged nits rather than failing outright) — this
// surfaces them for review instead of the silent-discard they used to get.
const getQaFlags = (guide: GeneratedGuide): QaFlag[] => {
  const qa = guide.pipeline_metadata?.qa as
    | { status?: string; open_issues?: Record<string, unknown[]> }
    | undefined;
  if (!qa || qa.status === 'ok' || !qa.open_issues) {
    return [];
  }
  const flags: QaFlag[] = [];
  for (const [stepKey, issues] of Object.entries(qa.open_issues)) {
    if (!Array.isArray(issues)) continue;
    for (const issue of issues) {
      flags.push({ stepKey, issue: formatQaIssue(issue) });
    }
  }
  return flags;
};

const formatTitleBullets = (bullets: string[]) =>
  bullets.map((bullet) => bullet.trim()).filter(Boolean).map((bullet) => `• ${bullet}`).join('\n');

const ShelfChipIcon = ({ iconId }: { iconId: string }) => {
  const iconById: Record<string, React.ElementType> = {
    small_space: Book,
    quick_cleanup: Zap,
    hard_floor: CheckCircle,
    pet_hair: Eye,
    stairs: Wrench,
    power: Zap,
    cordless: Zap,
    runtime: Eye,
    weight: Wrench,
    dust_volume: Book,
    large_area: Eye,
    continuous_power: Zap,
    deep_clean: Wrench,
    large_bin: Book,
    price: CheckCircle,
    stock: CheckCircle,
    location: Eye,
    warranty: CheckCircle
  };
  const Icon = iconById[iconId] || Circle;
  return <Icon className="h-4 w-4" aria-hidden="true" />;
};

const getStepTitleBullets = (step: GuideStep): string[] => {
  if (step.title_bullets?.length) {
    return [...step.title_bullets];
  }
  if (!step.title?.trim()) {
    return ['', '', ''];
  }
  const parsed = step.title
    .split('\n')
    .map((line) => line.replace(/^•\s*/, '').trim())
    .filter(Boolean);
  return parsed.length ? parsed : [''];
};

const GuideResults: React.FC<GuideResultsProps> = ({
  guides,
  productName,
  draftId,
  onExportGuide,
  onBack,
  onStartOver,
  makerCreatorId,
  makerBrands = [],
  selectedMakerBrandId,
  selectedMakerProductId,
  onMakerTargetChange,
  generationSettings,
  returnToEditorUrl
}) => {
  const [expandedGuides, setExpandedGuides] = useState<Set<string>>(new Set());
  const [editingGuides, setEditingGuides] = useState<{ [key: string]: GeneratedGuide }>({});
  const [draggedStep, setDraggedStep] = useState<{ guideId: string; stepIndex: number } | null>(null);
  const [expandedImagePickers, setExpandedImagePickers] = useState<Set<string>>(new Set());
  const [creatingSimpi, setCreatingSimpi] = useState(false);
  const [simpiResult, setSimpiResult] = useState<{
    status: 'running' | 'succeeded' | 'failed';
    message: string;
    jobStatusUrl?: string;
    editorUrl?: string;
    captureUrl?: string;
  } | null>(null);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const { healthStatus: orchestratorHealthStatus, checkHealth: checkOrchestratorHealth } = useOrchestratorHealth();

  const selectedBrand = makerBrands.find((b) => b.brandId === selectedMakerBrandId) || makerBrands[0];
  const brandProducts = selectedBrand?.products || [];
  const selectedProduct = brandProducts.find((p) => p.productId === selectedMakerProductId)
    || (selectedMakerProductId ? { productId: selectedMakerProductId, productName: selectedMakerProductId } : undefined);
  const hasProductCatalog = brandProducts.length > 0;
  const isTargetRequired = makerBrands.length > 0;
  const isTargetReady = !isTargetRequired || (!!selectedBrand?.brandId && !!selectedProduct?.productId);
  const selectedBrandAlias = selectedBrand?.brandAlias;
  const safeReturnToEditorUrl = React.useMemo(() => {
    if (!returnToEditorUrl) return null;
    try {
      const parsed = new URL(returnToEditorUrl);
      if (!/^https?:$/i.test(parsed.protocol)) return null;
      return parsed.toString();
    } catch {
      return null;
    }
  }, [returnToEditorUrl]);

  const submissionStorageKey = draftId ? `simpi_submission_${draftId}` : null;

  const getStepCapture = useCallback((step: GuideStep) => {
    if (step.capture?.perspective?.trim() && step.capture?.action?.trim()) {
      return {
        type: step.capture.type || 'photo',
        perspective: step.capture.perspective.trim(),
        action: step.capture.action.trim(),
        imageUrl: step.capture.imageUrl,
        colorHint: step.capture.colorHint
      } as const;
    }
    return {
      type: 'photo' as const,
      perspective: `Close-up view of ${step.title.toLowerCase()}`,
      action: step.description
    };
  }, []);

  const getListingImages = useCallback((guide: GeneratedGuide) => {
    const metadataImages = guide.pipeline_metadata?.product_listing_images;
    const imageUrls = Array.isArray(metadataImages)
      ? metadataImages.filter((imageUrl): imageUrl is string => typeof imageUrl === 'string' && imageUrl.trim().length > 0)
      : [];
    const stepImages = guide.steps
      .map((step) => step.capture?.imageUrl)
      .filter((imageUrl): imageUrl is string => typeof imageUrl === 'string' && imageUrl.trim().length > 0);
    return Array.from(new Set([...imageUrls, ...stepImages]));
  }, []);

  const toggleImagePicker = useCallback((pickerId: string) => {
    setExpandedImagePickers((prev) => {
      const next = new Set(prev);
      if (next.has(pickerId)) {
        next.delete(pickerId);
      } else {
        next.add(pickerId);
      }
      return next;
    });
  }, []);

  // Resume after refresh (best-effort)
  React.useEffect(() => {
    if (!submissionStorageKey) return;
    const existing = localStorage.getItem(submissionStorageKey);
    if (existing) setSubmissionId(existing);
  }, [submissionStorageKey]);

  // Initialize editing state for guides
  const getEditingGuide = useCallback((guideId: string) => {
    if (!editingGuides[guideId]) {
      const originalGuide = guides.find(g => g.id === guideId);
      if (originalGuide) {
        setEditingGuides(prev => ({
          ...prev,
          [guideId]: {
            ...originalGuide,
            steps: originalGuide.steps.map((step, index) => ({
              ...step,
              id: step.id || `step-${index}`
            }))
          }
        }));
      }
    }
    return editingGuides[guideId] || guides.find(g => g.id === guideId);
  }, [editingGuides, guides]);

  const updateGuideStep = useCallback((guideId: string, stepId: string, updates: Partial<GuideStep>) => {
    setEditingGuides(prev => {
      const guide = prev[guideId] || guides.find(g => g.id === guideId);
      if (!guide) return prev;

      const updatedSteps = guide.steps.map(step => 
        step.id === stepId ? { ...step, ...updates } : step
      );

      return {
        ...prev,
        [guideId]: {
          ...guide,
          steps: updatedSteps
        }
      };
    });
  }, [guides]);

  const selectStepImage = useCallback((guideId: string, step: GuideStep, imageUrl: string) => {
    const capture = getStepCapture(step);
    updateGuideStep(guideId, step.id, {
      fallback_image: false,
      capture: {
        ...capture,
        type: 'photo',
        imageUrl,
        colorHint: undefined
      }
    });
  }, [getStepCapture, updateGuideStep]);

  const updateStepTitleBullets = useCallback((guideId: string, step: GuideStep, bullets: string[]) => {
    const cleaned = bullets.map((bullet) => bullet.trim()).filter(Boolean);
    updateGuideStep(guideId, step.id, {
      title_bullets: cleaned,
      title: formatTitleBullets(cleaned)
    });
  }, [updateGuideStep]);

  const deleteStep = useCallback((guideId: string, stepId: string) => {
    setEditingGuides(prev => {
      const guide = prev[guideId] || guides.find(g => g.id === guideId);
      if (!guide) return prev;

      const updatedSteps = guide.steps.filter(step => step.id !== stepId);

      return {
        ...prev,
        [guideId]: {
          ...guide,
          steps: updatedSteps
        }
      };
    });
  }, [guides]);

  // AI content generation for new steps
  const generateStepContent = useCallback(async (guide: GeneratedGuide, insertIndex: number) => {
    try {
      const response = await creatorFetch(apiV1Url('/api/v1/generate-step-content'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          guide_title: guide.title,
          guide_description: guide.description,
          existing_steps: guide.steps.slice(0, insertIndex),
          following_steps: guide.steps.slice(insertIndex),
          product_name: productName,
          category: guide.category
        })
      });

      if (response.ok) {
        const data = await response.json();
        return data.step_content;
      }
    } catch (_error) {
      // Fallback below
    }

    // Fallback content
    return {
      title: `Step ${insertIndex + 1}`,
      description: 'New step description',
      visual_suggestion: 'Visual suggestion for this step',
      capture: {
        type: 'photo',
        perspective: 'Close-up view of the key part',
        action: 'Show the key action clearly'
      }
    };
  }, [productName]);

  const addStep = useCallback(async (guideId: string, insertIndex: number) => {
    const guide = getEditingGuide(guideId);
    if (!guide) return;

    // Generate new step content with AI
    const newStepContent = await generateStepContent(guide, insertIndex);

    const newStep: GuideStep = {
      id: `step-${Date.now()}`,
      order: insertIndex + 1,
      title: newStepContent.title,
      description: newStepContent.description,
      warning: newStepContent.warning,
      visual_suggestion: newStepContent.visual_suggestion,
      capture: newStepContent.capture || {
        type: 'photo',
        perspective: 'Close-up view of the step area',
        action: newStepContent.description || 'Show the key action clearly'
      }
    };

    setEditingGuides(prev => {
      const currentGuide = prev[guideId] || guide;
      const newSteps = [...currentGuide.steps];
      newSteps.splice(insertIndex, 0, newStep);

      return {
        ...prev,
        [guideId]: {
          ...currentGuide,
          steps: newSteps
        }
      };
    });
  }, [getEditingGuide, generateStepContent]);

  const reorderSteps = useCallback((guideId: string, fromIndex: number, toIndex: number) => {
    setEditingGuides(prev => {
      const guide = prev[guideId] || guides.find(g => g.id === guideId);
      if (!guide) return prev;

      const newSteps = [...guide.steps];
      const [movedStep] = newSteps.splice(fromIndex, 1);
      newSteps.splice(toIndex, 0, movedStep);

      return {
        ...prev,
        [guideId]: {
          ...guide,
          steps: newSteps
        }
      };
    });
  }, [guides]);

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, guideId: string, stepIndex: number) => {
    setDraggedStep({ guideId, stepIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, guideId: string, targetIndex: number) => {
    e.preventDefault();
    if (draggedStep && draggedStep.guideId === guideId) {
      reorderSteps(guideId, draggedStep.stepIndex, targetIndex);
    }
    setDraggedStep(null);
  };


  const toggleGuideExpansion = (guideId: string) => {
    const newExpanded = new Set(expandedGuides);
    if (newExpanded.has(guideId)) {
      newExpanded.delete(guideId);
    } else {
      newExpanded.add(guideId);
    }
    setExpandedGuides(newExpanded);
  };

  // SIMPI Integration Functions
  const handleCreateSimpis = async () => {
    setCreatingSimpi(true);
    try {
      console.log('🔍 Starting SIMPI creation process...');

      // Step 2: Build draft and start the orchestrator
      if (!draftId) {
        throw new Error('Missing draftId (version). Please regenerate guides and try again.');
      }
      if (!makerCreatorId) {
        throw new Error('Missing user identity. Please sign in again.');
      }
      if (isTargetRequired && (!selectedBrand?.brandId || !selectedProduct?.productId)) {
        throw new Error('Please select a brand and product before creating SIMPIs.');
      }
      console.log('🔍 Step 2: Creating submission and starting orchestrator...');
      const finalProductName = productName || 'Generated Product Guide';
      const editedGuides = guides.map((g) => getEditingGuide(g.id) || g);
      const submitCorrelationId = createCorrelationId('maker-submit');

      const submitRes = await creatorFetch(apiV1Url('/api/v1/simpi-submissions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Correlation-Id': submitCorrelationId
        },
        body: JSON.stringify({
          draftId,
          correlationId: submitCorrelationId,
          brandId: selectedBrand?.brandId,
          productId: selectedProduct?.productId,
          productName: finalProductName,
          guides: editedGuides,
          context: {
            // Maker expects base URLs that it can append resource paths to (e.g. /products)
            apiBaseUrl: 'https://api.simpi.com/api/v1',
            usersBaseUrl: 'https://users.simpi.com/api/v1',
            brandId: selectedBrand?.brandId,
            creatorId: makerCreatorId,
            locale: navigator.language || 'en-US'
          }
        })
      });

      if (!submitRes.ok) {
        const errorText = await submitRes.text();
        throw new Error(`Failed to submit (${submitRes.status}): ${errorText}`);
      }

      const submission = await submitRes.json();
      const newSubmissionId = submission.submissionId as string;
      setSubmissionId(newSubmissionId);
      if (submissionStorageKey) localStorage.setItem(submissionStorageKey, newSubmissionId);

      setSimpiResult({
        status: 'running',
        message: `Submission created. Tracking: ${newSubmissionId}`,
        jobStatusUrl: submission.jobStatusUrl as string | undefined
      });
      return;

      /* Legacy direct SIMPI creation removed (orchestrator is the single path)
         NOTE: image/banner generation endpoints have been removed from the backend;
         keep this block commented.
      // Step 2: Create product using the brandId from login response
      console.log('🔍 Step 2: Creating product...');
      const finalProductName = productName || 'Generated Product Guide';
      console.log('📝 Using product name:', finalProductName);
      
      const productPayload = {
        productId: null,
        brandId: homeBrandId,
        creatorId: loginData.id,
        deploymentState: 1,
        productName: finalProductName,
        productImageId: null,
        productPageImageId: null
      };
      
      console.log('📤 Product payload:', productPayload);
      
      const productResponse = await fetch('https://api.simpi.com/api/v1/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(productPayload)
      });

      if (!productResponse.ok) {
        const errorText = await productResponse.text();
        console.error('❌ Product creation failed:', productResponse.status, errorText);
        throw new Error(`Failed to create product (${productResponse.status}): ${errorText}`);
      }

      const productData = await productResponse.json();
      console.log('✅ Product created:', productData);
      const productAlias = productData.alias;
      
      // (Banner / media handling removed)

      // Step 3: Create SIMPIs and steps individually
      console.log('🔍 Step 3: Creating SIMPIs and steps individually...');
      
      let totalSimpisCreated = 0;
      let totalStepsCreated = 0;
      const createdSimpiIds: { [guideId: string]: string } = {};
      
      for (const guide of guides) {
        const currentGuide = getEditingGuide(guide.id) || guide;
        
        // Create SIMPI
        console.log(`🔍 Creating SIMPI: ${currentGuide.title}`);
        // Add number prefix to category for alphabetical sorting order
        let orderedGroupName = currentGuide.category;
        const category = currentGuide.category.toLowerCase();
        if (category.includes('setup') || category.includes('installation')) {
          orderedGroupName = `1. ${currentGuide.category}`;  // First group
        } else if (category.includes('operation') || category.includes('usage')) {
          orderedGroupName = `2. ${currentGuide.category}`;  // Second group
        } else {
          orderedGroupName = `3. ${currentGuide.category}`;  // Third group (maintenance, troubleshooting, safety)
        }
        
        const simpiPayload = {
          title: currentGuide.title,
          description: currentGuide.description,
          productId: productData.id,
          creatorId: loginData.id,
          groupName: orderedGroupName,  // Add numbered prefix for correct sorting
          showInfoOverlay: true,
          deploymentInfo: {
            deploymentState: 1,
            releaseDate: new Date().toISOString()
          }
        };
        
        const simpiResponse = await fetch('https://api.simpi.com/api/v1/simpis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(simpiPayload)
        });
        
        if (!simpiResponse.ok) {
          const errorText = await simpiResponse.text();
          console.error('❌ SIMPI creation failed:', simpiResponse.status, errorText);
          throw new Error(`Failed to create SIMPI "${currentGuide.title}" (${simpiResponse.status}): ${errorText}`);
        }
        
        const simpiData = await simpiResponse.json();
        console.log('✅ SIMPI created:', simpiData);
        totalSimpisCreated++;
        
        // Store the SIMPI ID for thumbnail generation
        createdSimpiIds[currentGuide.id] = simpiData.id;

        // Update SIMPI privacy (deploymentState 2: private group)
        try {
          const deployInfoPayload = {
            deletionDate: '0001-01-01T00:00:00Z',
            deploymentState: 2,
            releaseDate: new Date().toISOString()
          };
          const simpiPrivacyRes = await fetch(`https://api.simpi.com/api/v1/simpis/deploymentInfo/${simpiData.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(deployInfoPayload)
          });
          if (simpiPrivacyRes.ok) {
            console.log(`🔒 SIMPI ${simpiData.id} set to private group (deploymentState=2)`);
          } else {
            const t = await simpiPrivacyRes.text();
            console.warn('⚠️ Failed to set SIMPI privacy:', simpiPrivacyRes.status, t);
          }
        } catch (e) {
          console.warn('⚠️ Error updating SIMPI privacy:', e);
        }
        
        // Debug: Log guide data before creating steps
        console.log(`📊 Guide data for "${currentGuide.title}":`, {
          id: currentGuide.id,
          title: currentGuide.title,
          description: currentGuide.description,
          stepsCount: currentGuide.steps.length,
          steps: currentGuide.steps.map((step, index) => ({
            index: index + 1,
            id: step.id,
            title: step.title,
            description: step.description,
            order: step.order,
            titleType: typeof step.title,
            descriptionType: typeof step.description
          }))
        });
        
        // Create steps for this SIMPI
        for (let i = 0; i < currentGuide.steps.length; i++) {
          const step = currentGuide.steps[i];
          console.log(`🔍 Creating step ${i + 1}: ${step.title}`);
          console.log(`📝 Step title: "${step.title}"`);
          console.log(`📝 Step description: "${step.description}"`);
          
          const stepPayload = {
            title: '',  // Empty - will be set in update
            description: '',  // Empty - will be set in update
            simpiId: simpiData.id,
            thumbnailId: null,
            mediaType: null,
            portraitIndicator: {
              x1: 0.5,
              y1: 0.5
            },
            stickers: [],
            textBackgroundColor: '',
            textPositionY: 0,
            voiceOverEnabled: true,
            videoId: null,
            insertStepAfterStepId: null
          };
          
          console.log('📤 Step payload:', JSON.stringify(stepPayload, null, 2));
          
          // Log the actual step object to see if there are any issues
          console.log('🔍 Raw step object:', {
            id: step.id,
            title: step.title,
            description: step.description,
            order: step.order,
            hasMedia: !!step.media
          });
          
          const stepResponse = await fetch('https://api.simpi.com/api/v1/steps', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(stepPayload)
          });
          
          if (!stepResponse.ok) {
            const errorText = await stepResponse.text();
            console.error('❌ Step creation failed:', stepResponse.status, errorText);
            throw new Error(`Failed to create step "${step.title}" (${stepResponse.status}): ${errorText}`);
          }
          
          const stepData = await stepResponse.json();
          console.log('✅ Step created:', stepData);
          totalStepsCreated++;
          
          // Step 2: Generate fresh default image using current step content and upload it
          console.log('🔍 Generating fresh default image for step:', step.title);
          let thumbnailId: string | null = null;

          try {
            // Generate fresh default image using current step content
            console.log('🎨 Generating fresh SVG image with current content for step:', step.title);
            const capture = getStepCapture(step);
            const imageResponse = await creatorFetch(apiV1Url('/api/v1/generate-default-step-image'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                title: step.title,
                description: `${capture.perspective}. ${capture.action}`,
                category: currentGuide.category,
                step_index: i,
                total_steps: currentGuide.steps.length
              })
            });

            if (imageResponse.ok) {
              const imageData = await imageResponse.json();
              console.log('✅ Fresh SVG image generated:', imageData.url);

              // Fetch the SVG content
              const svgResponse = await fetch(imageData.url);
              if (svgResponse.ok) {
                const svgContent = await svgResponse.text();

                // Convert SVG to blob
                const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
                const imageFile = new File([svgBlob], `step-${step.id}-default.svg`, { type: 'image/svg+xml' });

                // Upload SVG to SIMPI
                const formData = new FormData();
                formData.append('stepImage', imageFile);

                const imageUploadResponse = await fetch('https://api.simpi.com/api/v1/steps/image', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  },
                  body: formData
                });

                if (imageUploadResponse.ok) {
                  const uploadData = await imageUploadResponse.json();
                  console.log('✅ Fresh SVG image uploaded:', uploadData);
                  thumbnailId = uploadData.imageId;
                } else {
                  console.warn('⚠️ SVG image upload failed, falling back to canvas generation');
                  thumbnailId = await generateCanvasImage(step, i, token);
                }
              } else {
                console.warn('⚠️ Could not fetch generated SVG image, falling back to canvas generation');
                thumbnailId = await generateCanvasImage(step, i, token);
              }
            } else {
              console.warn('⚠️ Failed to generate fresh default image, falling back to canvas generation');
              thumbnailId = await generateCanvasImage(step, i, token);
            }
          } catch (error) {
            console.error('❌ Error generating fresh default image:', error);
            thumbnailId = await generateCanvasImage(step, i, token);
          }

          // Step 3: Update step with title, description, and thumbnailId
          if (thumbnailId) {
            console.log(`🔄 Updating step ${stepData.id} with title, description, and image`);

            const updatePayload = {
              title: step.title || `Step ${i + 1}`,
              description: step.description || `Description for step ${i + 1}`,
              thumbnailId,
              mediaType: 'image',
              portraitIndicatorX1: 0.5,
              portraitIndicatorY1: 0.5,
              stickers: [],
              textBackgroundColor: '',
              textPositionY: 0,
              voiceOverEnabled: true,
              videoId: null
            };

            console.log('📤 Update payload:', JSON.stringify(updatePayload, null, 2));

            const updateResponse = await fetch(`https://api.simpi.com/api/v1/steps/${stepData.id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(updatePayload)
            });

            if (updateResponse.ok) {
              console.log('✅ Step updated with title, description, and image');
            } else {
              const errorText = await updateResponse.text();
              console.warn('⚠️ Step update failed:', updateResponse.status, errorText);
            }
          }
        }
      }
      
      console.log(`✅ Successfully created ${totalSimpisCreated} SIMPIs with ${totalStepsCreated} steps!`);
      
      // Generate and set thumbnails for all SIMPIs
      console.log('🎨 Generating thumbnails for SIMPIs...');
      try {
        const thumbnailResults = await generateAndSetThumbnails(guides, token, createdSimpiIds);
        console.log('✅ Thumbnail generation completed:', thumbnailResults);
      } catch (error) {
        console.warn('⚠️ Thumbnail generation failed:', error);
        // Don't fail the entire process if thumbnails fail
      }
      
      const simpiPlayerUrl = `https://app.simpi.com/${brandAlias}/product/${productAlias}/overview`;
      setSimpiResult({
        success: true,
        message: `Successfully created ${totalSimpisCreated} SIMPIs with ${totalStepsCreated} steps!`,
        url: simpiPlayerUrl
      });
      */
    } catch (error) {
      console.error('❌ SIMPI creation error:', error);
      setSimpiResult({
        status: 'failed',
        message: error instanceof Error ? error.message : 'Failed to start orchestrator'
      });
    } finally {
      setCreatingSimpi(false);
    }
  };

  // Poll submission status
  React.useEffect(() => {
    if (!submissionId) return;
    let cancelled = false;
    let t: number | null = null;

    const isRecord = (v: unknown): v is Record<string, unknown> =>
      !!v && typeof v === 'object' && !Array.isArray(v);

    const getString = (obj: Record<string, unknown>, key: string): string | undefined => {
      const v = obj[key];
      return typeof v === 'string' ? v : undefined;
    };

    const derivePlayerUrl = (result: unknown): string | undefined => {
      if (!isRecord(result)) return undefined;

      const direct =
        getString(result, 'playerUrl') ||
        getString(result, 'simpiPlayerUrl') ||
        getString(result, 'player_url') ||
        getString(result, 'simpi_player_url');
      if (direct && direct.startsWith('http')) return direct;

      const brandObj = isRecord(result.brand) ? (result.brand as Record<string, unknown>) : null;
      const productObj = isRecord(result.product) ? (result.product as Record<string, unknown>) : null;

      const brandAlias =
        getString(result, 'brandAlias') ||
        getString(result, 'brand_alias') ||
        getString(result, 'brand') ||
        (brandObj ? getString(brandObj, 'alias') : undefined);

      const productAlias =
        getString(result, 'productAlias') ||
        getString(result, 'product_alias') ||
        getString(result, 'product') ||
        (productObj ? getString(productObj, 'alias') : undefined);

      if (brandAlias && productAlias) {
        return `https://app.simpi.com/${brandAlias}/product/${productAlias}/overview`;
      }
      // Fallback: use selected dashboard brand alias when orchestrator result omits it.
      if (selectedBrandAlias && productAlias) {
        return `https://app.simpi.com/${selectedBrandAlias}/product/${productAlias}/overview`;
      }
      return undefined;
    };

    const deriveCaptureUrl = (result: unknown): string | undefined => {
      if (!isRecord(result)) return undefined;

      const direct =
        getString(result, 'captureUrl') ||
        getString(result, 'capture_url') ||
        getString(result, 'mobileUrl') ||
        getString(result, 'mobile_url');
      if (direct && direct.startsWith('http')) return direct;

      const brandObj = isRecord(result.brand) ? (result.brand as Record<string, unknown>) : null;
      const productObj = isRecord(result.product) ? (result.product as Record<string, unknown>) : null;

      const brandAlias =
        getString(result, 'brandAlias') ||
        getString(result, 'brand_alias') ||
        (brandObj ? getString(brandObj, 'alias') : undefined) ||
        selectedBrandAlias ||
        undefined;

      const productAlias =
        getString(result, 'productAlias') ||
        getString(result, 'product_alias') ||
        (productObj ? getString(productObj, 'alias') : undefined);

      const productId =
        getString(result, 'productId') ||
        getString(result, 'product_id') ||
        (productObj ? getString(productObj, 'id') || getString(productObj, 'productId') : undefined) ||
        selectedProduct?.productId ||
        selectedMakerProductId ||
        undefined;

      if (!productId) return undefined;

      const qs = new URLSearchParams();
      if (brandAlias) qs.set('brandAlias', brandAlias);
      if (productAlias) qs.set('productAlias', productAlias);
      const query = qs.toString();
      const baseUrl = `https://mobile.simpi.com/products/${encodeURIComponent(productId)}/simpis`;
      return query ? `${baseUrl}?${query}` : baseUrl;
    };

    const poll = async () => {
      try {
        const res = await creatorFetch(apiV1Url(`/api/v1/simpi-submissions/${submissionId}`));
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const status = data.status as string;
        if (status === 'succeeded') {
          const editorUrl = derivePlayerUrl(data.result);
          const captureUrl = deriveCaptureUrl(data.result);
          setSimpiResult({
            status: 'succeeded',
            message: 'SIMPIs created successfully!',
            jobStatusUrl: data.jobStatusUrl || undefined,
            editorUrl,
            captureUrl
          });
          if (t) window.clearInterval(t);
          t = null;
          return;
        }
        if (status === 'failed') {
          setSimpiResult({
            status: 'failed',
            message: data.lastError || 'Orchestrator failed.',
            jobStatusUrl: data.jobStatusUrl || undefined
          });
          if (t) window.clearInterval(t);
          t = null;
          return;
        }
        // Still running: keep polling, but don't spam modal updates.
      } catch {
        // ignore
      }
    };
    t = window.setInterval(poll, 2000);
    poll();
    return () => {
      cancelled = true;
      if (t) window.clearInterval(t);
    };
  }, [submissionId, selectedBrandAlias, selectedMakerProductId, selectedProduct?.productId]);



  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'setup':
        return <Book className="w-5 h-5" />;
      case 'maintenance':
        return <Wrench className="w-5 h-5" />;
      case 'troubleshooting':
        return <HelpCircle className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'hard':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'setup':
        return 'bg-blue-100 text-blue-800';
      case 'maintenance':
        return 'bg-purple-100 text-purple-800';
      case 'troubleshooting':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // NOTE: Thumbnail generation helpers were part of a previous direct-SIMPI flow which is
  // currently commented out in this component. Re-introduce them when that flow is revived.

  return (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">
          {generationSettings?.template
            ? `${generationSettings.template.replace('_', ' ')} Generated Successfully!`
            : 'Guides Generated Successfully!'}
        </h2>
        <p className="text-gray-600">
          {guides.length} step-by-step guide{guides.length > 1 ? 's' : ''} created for {productName}
        </p>
      </div>

      {/* Create SIMPI Button - Top Left */}
      <div className="space-y-3">
        {makerBrands.length > 0 && (
          <div className="bg-white border rounded-md p-3">
            <p className="text-sm font-medium text-gray-700 mb-2">Target product (Dashboard authorization)</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Product</label>
                {hasProductCatalog ? (
                  <select
                    className="w-full border rounded px-2 py-1 text-sm"
                    value={selectedProduct?.productId || ''}
                    onChange={(e) => onMakerTargetChange?.(selectedBrand?.brandId || '', e.target.value)}
                  >
                    <option value="">Select a product</option>
                    {brandProducts.map((p) => (
                      <option key={p.productId} value={p.productId}>{p.productName}</option>
                    ))}
                  </select>
                ) : (
                  <div className="w-full border rounded px-2 py-1 text-sm bg-gray-50 text-gray-700">
                    {selectedProduct?.productId || 'No product context'}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Brand</label>
                <div className="w-full border rounded px-2 py-1 text-sm bg-gray-50 text-gray-700">
                  {selectedBrand?.brandName || 'No brand context'}
                </div>
              </div>
            </div>
          </div>
        )}

      <div className="flex justify-start">
        <button
          onClick={async () => {
            await checkOrchestratorHealth();
            await handleCreateSimpis();
          }}
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={creatingSimpi || !isTargetReady || !makerCreatorId}
        >
          {creatingSimpi ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {guides.length === 1 ? 'Creating SIMPI...' : 'Creating All SIMPIs...'}
            </>
          ) : (
            <>
              <Zap className="w-4 h-4 mr-2" />
              {guides.length === 1 ? 'Create SIMPI' : `Create SIMPIs (${guides.length})`}
            </>
          )}
        </button>
      </div>
      {!makerCreatorId && (
        <div className="text-sm text-amber-700">
          Sign in with Entra to create SIMPIs.
        </div>
      )}
      </div>

      <OrchestratorStatus healthStatus={orchestratorHealthStatus} />

      <div className="space-y-4">
        {guides.map((guide) => {
          const isExpanded = expandedGuides.has(guide.id);
          const qaFlags = getQaFlags(guide);

          return (
            <div key={guide.id} className="overflow-hidden border rounded-lg shadow-sm bg-white">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      {getCategoryIcon(guide.category)}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-2">{guide.title}</h3>
                      <p className="text-gray-600 mb-3">{guide.description}</p>
                      
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(guide.category)}`}>
                          {guide.category}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(guide.difficulty)}`}>
                          {guide.difficulty}
                        </span>
                        <div className="flex items-center space-x-1 text-gray-500">
                          <FileText className="w-3 h-3" />
                          <span>{guide.estimated_time || 'Variable'}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-gray-500">
                          <CheckCircle className="w-3 h-3" />
                          <span>{guide.steps.length} steps</span>
                        </div>
                      </div>

                      {qaFlags.length > 0 && (
                        <div className="mt-3 flex items-start space-x-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">
                              {qaFlags.length} quality {qaFlags.length === 1 ? 'issue' : 'issues'} flagged for review — generation still completed
                            </p>
                            <ul className="mt-1 space-y-0.5 text-amber-700">
                              {qaFlags.map((flag, index) => (
                                <li key={`${flag.stepKey}-${index}`}>
                                  <span className="font-mono text-xs">{flag.stepKey}</span>: {flag.issue}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
                      onClick={() => toggleGuideExpansion(guide.id)}
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          Collapse
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-1" />
                          Expand
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold">Steps:</h4>
                    </div>
                    
                    <div className="space-y-2">
                      {/* Add step button at the beginning */}
                      <div className="flex justify-center">
                        <button
                          onClick={() => addStep(guide.id, 0)}
                          className="flex items-center px-3 py-1 border border-dashed border-gray-300 rounded-md text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Step
                        </button>
                      </div>

                      {(getEditingGuide(guide.id)?.steps || []).map((step, index) => {
                        const listingImages = getListingImages(guide);
                        const imagePickerId = `${guide.id}:${step.id}`;
                        const isImagePickerExpanded = expandedImagePickers.has(imagePickerId);

                        return (
                        <div key={step.id}>
                          <div
                            className={`group relative border rounded-lg p-4 transition-all ${
                              draggedStep?.guideId === guide.id && draggedStep?.stepIndex === index
                                ? 'opacity-50 bg-gray-50'
                                : 'bg-white hover:shadow-md'
                            }`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, guide.id, index)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, guide.id, index)}
                          >
                            {/* Step drag handle */}
                            <div className="absolute left-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-move">
                              <GripVertical className="w-4 h-4 text-gray-400" />
                            </div>

                            <div className="flex space-x-3 ml-6">
                              <div className="flex-1">
                                {/* Step title - inline editable */}
                                <div className="mb-2">
                                  {isShelfWalkthrough(guide) ? (
                                    <div className="space-y-2">
                                      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                        Step title
                                      </div>
                                      {step.step_titles?.length ? (
                                        <div className="flex flex-wrap gap-2">
                                          {step.step_titles.map((titleItem, titleIndex) => (
                                            <div
                                              key={`${step.id}-title-${titleIndex}`}
                                              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${
                                                titleItem.render_style === 'grayed_slash'
                                                  ? 'border-gray-300 bg-gray-100 text-gray-500'
                                                  : 'border-amber-200 bg-amber-50 text-gray-800'
                                              }`}
                                            >
                                              <ShelfChipIcon iconId={titleItem.icon_id} />
                                              <span className={titleItem.render_style === 'grayed_slash' ? 'line-through' : ''}>
                                                {titleItem.label}
                                              </span>
                                              {titleItem.context_tag && (
                                                <span className="text-xs text-gray-500">{titleItem.context_tag}</span>
                                              )}
                                              {titleItem.redirect && (
                                                <span className="ml-1 font-medium text-gray-700">→ {titleItem.redirect}</span>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        getStepTitleBullets(step).map((bullet, bulletIndex) => (
                                          <div key={`${step.id}-bullet-${bulletIndex}`} className="flex items-start gap-2">
                                            <span className="pt-2 text-amber-600">•</span>
                                            <input
                                              type="text"
                                              value={bullet}
                                              onChange={(e) => {
                                                const nextBullets = [...getStepTitleBullets(step)];
                                                nextBullets[bulletIndex] = e.target.value;
                                                updateStepTitleBullets(guide.id, step, nextBullets);
                                              }}
                                              className="w-full font-medium text-base bg-transparent border-none outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white rounded px-2 py-1"
                                              placeholder={`Step title ${bulletIndex + 1}`}
                                            />
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  ) : (
                                    <input
                                      type="text"
                                      value={step.title}
                                      onChange={(e) => updateGuideStep(guide.id, step.id, { title: e.target.value })}
                                      className="w-full font-medium text-lg bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white rounded px-2 py-1"
                                      placeholder="Step title"
                                    />
                                  )}
                                </div>

                                {/* Step description = TTS / spoken channel */}
                                <div className="mb-3 space-y-2">
                                  <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                                    {isShelfWalkthrough(guide) ? 'Step description (TTS)' : 'Step description'}
                                  </div>
                                  <textarea
                                    value={step.description}
                                    onChange={(e) => updateGuideStep(guide.id, step.id, { description: e.target.value })}
                                    className={`w-full text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white rounded px-2 py-1.5 resize-none ${
                                      isShelfWalkthrough(guide)
                                        ? 'border border-gray-200 bg-white text-gray-800'
                                        : 'border-none bg-transparent text-gray-600'
                                    }`}
                                    placeholder={isShelfWalkthrough(guide) ? 'Spoken line for TTS' : 'Step description'}
                                    rows={isShelfWalkthrough(guide) ? 3 : 2}
                                  />
                                </div>



                                {step.action_brief && (
                                  <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                                    <div className="mb-2 font-medium text-slate-700">
                                      Action visual · {step.action_brief.mode}
                                    </div>
                                    <div className="space-y-1 text-slate-600">
                                      {[
                                        ['Action', step.action_brief.subject_action],
                                        ['Camera', step.action_brief.camera],
                                        ['Show', step.action_brief.must_show],
                                        ['Avoid', step.action_brief.must_not_show],
                                        ['End frame', step.action_brief.end_frame],
                                        ['Still detail', step.action_brief.detail]
                                      ].filter(([, value]) => value).map(([label, value]) => (
                                        <div key={label}>
                                          <span className="font-medium">{label}:</span> {value}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Warning - inline editable */}
                                {step.warning && (
                                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                    <div className="flex items-start space-x-2">
                                      <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                      <textarea
                                        value={step.warning}
                                        onChange={(e) => updateGuideStep(guide.id, step.id, { warning: e.target.value })}
                                        className="flex-1 text-yellow-800 text-sm bg-transparent border-none outline-none focus:ring-2 focus:ring-yellow-500 rounded px-1 py-0.5 resize-none"
                                        placeholder="Warning message"
                                        rows={1}
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Visual suggestion - inline editable */}
                                {step.visual_suggestion && (
                                  <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <div className="flex items-start space-x-2">
                                      <span className="text-blue-800">💡</span>
                                      <textarea
                                        value={step.visual_suggestion}
                                        onChange={(e) => updateGuideStep(guide.id, step.id, { visual_suggestion: e.target.value })}
                                        className="flex-1 text-blue-800 text-sm bg-transparent border-none outline-none focus:ring-2 focus:ring-blue-500 rounded px-1 py-0.5 resize-none"
                                        placeholder="Visual suggestion"
                                        rows={1}
                                      />
                                    </div>
                                  </div>
                                )}

                                {(() => {
                                  const capture = getStepCapture(step);
                                  return (
                                    <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
                                      <div className="flex items-start space-x-2">
                                        <span className="text-gray-700">📷</span>
                                        <div className="flex-1">
                                          <p className="text-gray-800 text-xs font-medium mb-2">Capture</p>
                                          <div className="mb-3 flex flex-col gap-3 sm:flex-row">
                                            {capture.imageUrl && (
                                              <div className="flex-shrink-0">
                                                <img
                                                  src={capture.imageUrl}
                                                  alt={`Selected visual for ${step.title}`}
                                                  className="h-24 w-24 rounded-md border border-gray-300 object-contain"
                                                />
                                              </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-xs text-gray-600">
                                                  {capture.imageUrl ? 'Selected listing image' : 'No listing image selected for this step'}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={() => toggleImagePicker(imagePickerId)}
                                                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                                >
                                                  {isImagePickerExpanded ? 'Hide images' : `Choose image (${listingImages.length})`}
                                                </button>
                                              </div>
                                              {isImagePickerExpanded && (
                                                <div className="mt-3 rounded-md border border-gray-200 bg-white p-2">
                                                  {listingImages.length > 0 ? (
                                                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
                                                      {listingImages.map((imageUrl) => {
                                                        const isSelected = capture.imageUrl === imageUrl;
                                                        return (
                                                          <button
                                                            key={imageUrl}
                                                            type="button"
                                                            onClick={() => selectStepImage(guide.id, step, imageUrl)}
                                                            className={`relative rounded-md border p-1 ${
                                                              isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-blue-300'
                                                            }`}
                                                            title="Select this image for the step"
                                                          >
                                                            <img
                                                              src={imageUrl}
                                                              alt="Listing option"
                                                              className="h-16 w-full rounded object-contain"
                                                            />
                                                            {isSelected && (
                                                              <span className="absolute right-1 top-1 rounded bg-blue-600 px-1 text-[10px] font-medium text-white">
                                                                Selected
                                                              </span>
                                                            )}
                                                          </button>
                                                        );
                                                      })}
                                                    </div>
                                                  ) : (
                                                    <p className="text-xs text-gray-500">No listing images were included in this result.</p>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="mb-2">
                                            <label className="text-xs text-gray-700 block mb-1">Type</label>
                                            <select
                                              value={capture.type}
                                              onChange={(e) =>
                                                updateGuideStep(guide.id, step.id, {
                                                  capture: {
                                                    ...capture,
                                                    type: e.target.value as 'photo' | 'video' | 'background'
                                                  }
                                                })
                                              }
                                              className="text-sm border border-gray-300 rounded px-2 py-1 bg-white text-gray-800"
                                            >
                                              <option value="photo">photo</option>
                                              <option value="video">video</option>
                                            </select>
                                          </div>
                                          <div className="mb-2">
                                            <label className="text-xs text-gray-700 block mb-1">Perspective</label>
                                            <textarea
                                              value={capture.perspective}
                                              onChange={(e) =>
                                                updateGuideStep(guide.id, step.id, {
                                                  capture: {
                                                    ...capture,
                                                    perspective: e.target.value
                                                  }
                                                })
                                              }
                                              className="w-full text-gray-800 text-sm bg-white border border-gray-300 outline-none focus:ring-2 focus:ring-gray-400 rounded px-2 py-1 resize-none"
                                              placeholder="Close-up, top-down, over-the-shoulder..."
                                              rows={2}
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-gray-700 block mb-1">Action</label>
                                            <textarea
                                              value={capture.action}
                                              onChange={(e) =>
                                                updateGuideStep(guide.id, step.id, {
                                                  capture: {
                                                    ...capture,
                                                    action: e.target.value
                                                  }
                                                })
                                              }
                                              className="w-full text-gray-800 text-sm bg-white border border-gray-300 outline-none focus:ring-2 focus:ring-gray-400 rounded px-2 py-1 resize-none"
                                              placeholder="Show the exact action (no timing or figure refs)"
                                              rows={2}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Step actions */}
                              {/* Media thumbnail - positioned on the right */}
                              {step.media && (
                                <div className="flex-shrink-0 relative">
                                  {step.media.type === 'image' ? (
                                    <>
                                      <img
                                        src={step.media.url}
                                        alt={step.media.description}
                                        className="w-16 h-16 object-cover rounded border"
                                      />
                                      {step.media.is_default && (
                                        <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded-full">
                                          AI
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="relative w-16 h-16">
                                      <video
                                        src={step.media.url}
                                        className="w-16 h-16 object-cover rounded border"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded">
                                        <Play className="w-6 h-6 text-white" />
                                      </div>
                                      {step.media.is_default && (
                                        <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs px-1 py-0.5 rounded-full">
                                          AI
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Step actions */}
                              <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => deleteStep(guide.id, step.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete step"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => addStep(guide.id, index + 1)}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Add step after this one"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between items-center pt-6">
        <button 
          onClick={onBack} 
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Back to Topics
        </button>
        
        <div className="flex space-x-4">
          <button 
            onClick={onStartOver} 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Start Over
          </button>
          <button 
            onClick={() => {
              // Expand all guides for better overview
              setExpandedGuides(new Set(guides.map(g => g.id)));
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Expand All
          </button>
        </div>
      </div>

      {/* SIMPI Success/Error Message */}
      {simpiResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-lg w-full mx-4 p-6">
            <div className="text-center">
              {simpiResult.status === 'succeeded' ? (
                <div>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">SIMPIs Created Successfully!</h3>
                  <p className="text-gray-600 mb-4">{simpiResult.message}</p>
                  <div className="flex flex-col items-center gap-3">
                    <a
                      href={safeReturnToEditorUrl || simpiResult.editorUrl || 'https://app.simpi.com'}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      Back to SIMPI Editor
                    </a>

                    {!safeReturnToEditorUrl && !simpiResult.editorUrl && (
                      <div className="text-sm text-gray-600">
                        Returned to SIMPI home because no explicit editor return URL was provided.
                      </div>
                    )}

                    <div className="h-3" />

                    {simpiResult.captureUrl ? (
                      <>
                        <div className="text-sm text-gray-600">Scan to open the product in the SIMPI mobile app</div>
                        <img
                          alt="QR code to open SIMPI mobile capture app"
                          className="mx-auto border rounded-md"
                          width={220}
                          height={220}
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                            simpiResult.captureUrl
                          )}`}
                        />
                        <a
                          href={simpiResult.captureUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Open in SIMPI Capture app
                        </a>
                      </>
                    ) : (
                      <div className="text-sm text-gray-600">
                        Created successfully, but couldn’t derive the mobile capture link. Please open the SIMPI mobile app and find the product.
                      </div>
                    )}
                  </div>
                </div>
              ) : simpiResult.status === 'running' ? (
                <div>
                  <Loader2 className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-spin" />
                  <h3 className="text-xl font-semibold text-blue-800 mb-2">Creating SIMPIs…</h3>
                  <p className="text-gray-600 mb-4">{simpiResult.message}</p>
                </div>
              ) : (
                <div>
                  <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-red-800 mb-2">Failed to Create SIMPIs</h3>
                  <p className="text-gray-600 mb-4">{simpiResult.message}</p>
                </div>
              )}
              <button
                onClick={() => setSimpiResult(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuideResults;